







































































import Vue from 'vue'
import * as moment from 'moment'
import { BvTableFieldArray } from 'bootstrap-vue'
import { Location } from 'vue-router'
import { each, filter, find, findIndex, map, some, startsWith, trimStart } from 'lodash-es'

import { ASMT_ADM, ASMT_SPECIALITY, ASMT_WARD } from 'routers/constants'
import utils from 'utils'
import { safeDecoding } from 'utils/store'
import {
    Patient as PPatient, anonymisedPatient,
    PatientData, patientDataDecoder,
    PatientDataExt,
    PatientStay, PaddockStay, Combined,
} from 'models/paddock'
import { Speciality } from 'models/med_templates/speciality'

import Checkbox from '@shared/inputs/Checkbox.vue'
import SandboxBtn from '@misc/SandboxBtn.vue'
import StayBtn from './PaddockSearchStay.vue'

export default Vue.extend({
    components: {
        Checkbox,
        SandboxBtn,
        StayBtn,
    },
    data() {
        const uid = utils.getUID()
        return {
            uid,
            htmlIDs: {
                search: `${uid}___search`
            },
            ready: false,
            search: '',
            patientData: [] as PatientData[],
            /** list of stay_ids that are currently being updated */
            updating: [] as number[],
            ascOrdering: true,
            cutoffHrs: 12,
            expanded: {} as { [index: number]: boolean },
        }
    },
    computed: {
        isPowerUser(): boolean {
            return this.$store.direct.getters.user.isPowerUser
        },
        consultsEnabled(): boolean {
            return this.$store.direct.state.session.ux_consult
        },
        selectedSpecialities(): number[] {
            return this.$store.direct.state.user.filters.specialities
        },
        /** list of speciality codes for non auto-generated specialities */
        specialityCodes(): string[] {
            return map(this.$store.direct.state.templates.specialities, sp => sp.code)
        },
        speciality(): Speciality | undefined {
            if (this.selectedSpecialities.length !== 1)
                return
            return this.$store.direct.getters.templates.speciality(this.selectedSpecialities[0])
        },
        specialityCode(): string | undefined {
            return this.speciality?.code
        },
        fields(): BvTableFieldArray {
            const self = this
            let fields: BvTableFieldArray = [
                {
                    key: 'mrn',
                    label: 'MRN',
                    formatter(val, key, item: PatientData): string {
                        return item.patient.mrn
                    },
                    thStyle: {
                        'width': '10%'
                    },
                },
                {
                    key: 'patient',
                    formatter(val: PPatient): string {
                        return self.formatPatient(val)
                    },
                    thStyle: {
                        'width': '20%'
                    },
                },
                {
                    key: 'all_stays',
                    thStyle: {
                        'width': this.isPowerUser ? '40%' : '70%'
                    },
                },
            ]

            if (this.isPowerUser)
                fields.push({
                    key: 'stays_raw',
                    thStyle: {
                        'width': '30%'
                    },
                })

            return fields
        },
        items(): PatientDataExt[] {
            const cutoff_m = moment().subtract(this.cutoffHrs, 'hours')
            return map(this.patientData, data => {
                const stays = data.stays.slice()
                const olds: Combined[] = []
                const news: Combined[] = []

                each(data.stays, stay => {
                    if (moment(stay.admitted_at).isBefore(cutoff_m))
                        olds.push(stay)
                    else
                        news.push(stay)
                })

                if (news.length === 0)
                    if (olds.length > 0 && olds[olds.length - 1].sstay?.active)
                        news.splice(0, 0, olds.splice(olds.length - 1, 1)[0])

                if (!this.ascOrdering)
                    olds.reverse()

                return {
                    patient: data.patient,
                    stays,
                    olds,
                    news,
                }
            })
        },
        orderingText(): string {
            return this.ascOrdering ? 'old > new' : 'new > old'
        },
    },
    methods: {
        consultHtmlID(pstay: PaddockStay): string {
            return `${this.uid}___${pstay.id}_consult_modal`
        },
        navigateToSpecialitySelection(stayId: number) {
            this.$router.push({
                name: ASMT_SPECIALITY,
                params: {
                    stay_id: String(stayId),
                }
            })
        },
        toggleExpanded(id: number): void {
            this.expanded[id] = !this.expanded[id]
        },
        showHideText(id: number): string {
            return this.expanded[id] ? 'Hide old -' : 'Show old +'
        },
        findAll() {
            this.ready = false

            if (startsWith(this.search, '0'))
                this.search = trimStart(this.search, '0')

            utils.request.get(`/paddock/stays/find/v2/?search=${this.search}`)
            .then(res => {
                this.patientData = safeDecoding(res.body, patientDataDecoder, 'PaddockSearch_v2.findAll')
                each(this.patientData, info => { Vue.set(this.expanded, info.patient.id, false) })
            })
            .catch(err => {
                utils.handleRequestError(err)
            })
            .then(res => {
                this.ready = true
            })
        },
        updateByMRN(mrn: string) {
            return utils.request.get(`/paddock/stays/find/v2/?search=${mrn}`)
            .then(res => {
                const patientData = safeDecoding(res.body, patientDataDecoder, 'PaddockSearch_v2.findByMRN')
                each(patientData, newPData => {
                    const index = findIndex(this.patientData, localPData => localPData.patient.mrn === newPData.patient.mrn)
                    if (index !== -1)
                        Vue.set(this.patientData, index, newPData)
                })
            })
            .catch(err => {
                utils.handleRequestError(err)
            })
        },
        canConsult(data: PatientData): boolean {
            if (some(data.stays, stayData => !!stayData.sstay && stayData.sstay.active)) return false
            return true
        },
        admitAndConsult(mrn: string, pstay_id: number) {
            const criteriaLogic = this.$store.direct.state.session.feedLogic === 'CRITERIA'
            const admitPromise = criteriaLogic ?
                utils.request.post(`/feeds/admit/${pstay_id}/`) :
                utils.request.patch(`/paddock/stay/${pstay_id}/`, false, { admit_state: 'ADMITTED' })

            admitPromise
            .then(res => {
                // const pstay: PStay = safeDecoding(res.body, stayDecoder, 'PaddockSearch_v2.admitPStay')
                return this.updateByMRN(mrn)
            })
            .then(res => {
                const pData = find(this.patientData, pData => pData.patient.mrn === mrn)
                if (pData) {
                    const combined = find(pData.stays, stays => stays.pstay?.id === pstay_id)
                    if (combined && combined.sstay)
                        this.consult(combined.sstay.id)
                }
            })
            .catch(err => {
                utils.handleRequestError(err)
            })
        },
        consult(stay_id: number) {
            this.navigateToSpecialitySelection(stay_id)
            /*
            utils.request.post(`/stay/consult/${stay_id}/`)
            .then(res => {
                if (res.status === 201)
                    this.navigateToSpecialitySelection(res.body.id)
                else
                    Promise.reject(res)
            })
            .catch(err => {
                utils.handleRequestError(err)
            })
            */
        },
        startConsult(patient: PPatient, combined: Combined) {
            if (combined.sstay) {
                this.consult(combined.sstay.id)
            }
            else if (combined.pstay) {
                this.admitAndConsult(patient.mrn, combined.pstay.id)
            }
        },
        startConsultByEvent(data: { patient: PPatient, combined: Combined }): void {
            this.startConsult(data.patient, data.combined)
        },
        formatDT(dt_str?: string) {
            if (!dt_str)
                return '-'
            const m = moment(dt_str)
            return m.format("h:mma Do MMM YYYY")
        },
        formatPatient(origPatient: PPatient) {
            const patient = this.$store.direct.state.user.anonymise ? anonymisedPatient(origPatient) : origPatient
            return `${patient.last_name}, ${patient.first_name}`
        },
        resetFilters() {
            this.search = ''
        },
        dataEntryRoute(sstay: PatientStay): Location {
            switch (sstay.stage) {
                case 'UNKNOWN':
                    return {
                        name: ASMT_SPECIALITY,
                        params: { stay_id: `${sstay.id}` }
                    }
                case 'ADMISSION':
                    return {
                        name: ASMT_ADM,
                        params: { stay_id: `${sstay.id}` }
                    }
                case 'WARD':
                    return {
                        name: ASMT_WARD,
                        params: { stay_id: `${sstay.id}` }
                    }
            }
            return {}
        },
        dataEntryLabel(sstay: PatientStay): string {
            let entryLabel = '?'
            switch (sstay.stage) {
                case 'ADMISSION':
                    entryLabel = 'Admission'
                    break
                case 'WARD':
                    entryLabel = 'Review'
                    break
                case 'UNKNOWN':
                    entryLabel = 'Start'
                    break
            }
            return entryLabel
        },
    }
})
